import { registerLuaDefinition } from "../CodeEditor";

/**
 * Types for floor button configurations
 */
export type ButtonType = 'SingleCall' | 'UpDown' | 'NumberedFloors';
registerLuaDefinition(`
--- @alias ButtonType "'SingleCall'" | "'UpDown'" | "'NumberedFloors'"
`);

export type VisitorDistribution = 'Random' | 'Interval' | 'Burst' | 'Sine';
registerLuaDefinition(`
--- @alias VisitorDistribution "'Random'" | "'Interval'" | "'Burst'" | "'Sine'"
`);

/**
 * Floor configuration
 */
export interface FloorConfig {
    buttonType: ButtonType;
}
registerLuaDefinition(`
--- @class FloorConfig
--- @field buttonType ButtonType
`);

/**
 * Lift configuration
 */
export interface LiftConfig {
    weightLimit: number;
    bottomFloor: number;
    topFloor: number;
    startingFloor: number;
    liftMoveSpeedSeconds: number;
}
registerLuaDefinition(`
--- @class LiftConfig
--- @field weightLimit number
--- @field bottomFloor number
--- @field topFloor number
--- @field startingFloor number
--- @field liftMoveSpeedSeconds number
`);

/**
 * Mission configuration set before game starts
 */
export interface MissionConfig {
    floors: FloorConfig[];
    lifts: Record<string, LiftConfig>;
    seedId?: number;
    maxVisitors?: number;
    visitorDistribution?: VisitorDistribution;
}
registerLuaDefinition(`
--- @class MissionConfig
--- @field floors FloorConfig[]
--- @field lifts table<string, LiftConfig>
--- @field seedId number | nil
--- @field maxVisitors number | nil
--- @field visitorDistribution VisitorDistribution | nil
`);

export const MISSION_DEFAULTS = {
    numberOfFloors: 5,
    numberOfLifts: 2,
    defaultButtonType: 'SingleCall' as ButtonType,
    defaultWeightLimit: 100,
    defaultLiftMoveSpeedSeconds: 2,
    defaultSeed: Date.now(),
    defaultMaxVisitors: 50,
    defaultVisitorDistribution: 'Random' as VisitorDistribution,
} as const;

export function createDefaultFloorConfig(): FloorConfig {
    return {
        buttonType: MISSION_DEFAULTS.defaultButtonType,
    };
}

export function createDefaultLiftConfig(numberOfFloors: number): LiftConfig {
    return {
        weightLimit: MISSION_DEFAULTS.defaultWeightLimit,
        bottomFloor: 0,
        topFloor: numberOfFloors - 1,
        startingFloor: 0,
        liftMoveSpeedSeconds: MISSION_DEFAULTS.defaultLiftMoveSpeedSeconds,
    };
}

export function createDefaultMissionConfig(): MissionConfig {
    const floors: FloorConfig[] = [];
    for (let i = 0; i < MISSION_DEFAULTS.numberOfFloors; i++) {
        floors.push(createDefaultFloorConfig());
    }

    const lifts: Record<string, LiftConfig> = {};
    for (let i = 0; i < MISSION_DEFAULTS.numberOfLifts; i++) {
        lifts[`lift${i}`] = createDefaultLiftConfig(MISSION_DEFAULTS.numberOfFloors);
    }

    return {
        floors,
        lifts,
        seedId: Date.now(),
        maxVisitors: MISSION_DEFAULTS.defaultMaxVisitors,
        visitorDistribution: MISSION_DEFAULTS.defaultVisitorDistribution
    };
}
